import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(135deg, var(--color-background) 0%, #1a1a1a 100%);

  @media (max-width: 768px) {
    padding: 5rem 1rem 3rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  padding: 3rem;
  border-radius: 30px;
  border: 4px solid var(--color-primary);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: 3rem;
  color: var(--color-primary);
  text-align: center;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  font-weight: 600;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.8rem;
  color: var(--color-accent);
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const Paragraph = styled.p`
  color: #1a1a1a;
  line-height: 1.8;
  margin-bottom: 1rem;
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 500;
`;

const List = styled.ul`
  color: #1a1a1a;
  line-height: 1.8;
  margin-left: 2rem;
  margin-bottom: 1rem;

  li {
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 229, 92, 0.1);
  padding: 1.5rem;
  border-radius: 15px;
  border: 2px solid var(--color-primary);
  margin-top: 2rem;
  text-align: center;

  a {
    color: var(--color-primary);
    font-weight: 700;
    text-decoration: underline;
  }
`;

const PrivacyPage: React.FC = () => {
  return (
    <PageContainer>
      <ContentWrapper>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last Updated: December 3, 2025</LastUpdated>

        <Section>
          <SectionTitle>1. Introduction</SectionTitle>
          <Paragraph>
            Welcome to Pepe's Dog Army! We respect your privacy and are committed to protecting your personal
            information. This Privacy Policy explains how we collect, use, and safeguard your data when
            you visit our website and interact with our community.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Information We Collect</SectionTitle>
          <Paragraph>
            We may collect the following types of information:
          </Paragraph>
          <List>
            <li><strong>Wallet Information:</strong> When you connect your Web3 wallet, we may access your public wallet address</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our website (pages visited, time spent, etc.)</li>
            <li><strong>Technical Data:</strong> IP address, browser type, device information, and operating system</li>
            <li><strong>Communication Data:</strong> Information you provide when contacting us via email</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. How We Use Your Information</SectionTitle>
          <Paragraph>
            We use the collected information for the following purposes:
          </Paragraph>
          <List>
            <li>To provide and improve our services</li>
            <li>To verify token holdings and enable holder benefits</li>
            <li>To communicate with you about updates and community events</li>
            <li>To analyze website usage and optimize user experience</li>
            <li>To detect and prevent fraudulent activities</li>
            <li>To comply with legal obligations</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Blockchain Data</SectionTitle>
          <Paragraph>
            Please note that blockchain transactions are public and permanent. When you interact with
            smart contracts or send cryptocurrency:
          </Paragraph>
          <List>
            <li>Your wallet address and transaction details are publicly visible on the blockchain</li>
            <li>This data cannot be deleted or modified</li>
            <li>Anyone can view your transaction history and token holdings</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>5. Cookies and Tracking Technologies</SectionTitle>
          <Paragraph>
            We may use cookies and similar tracking technologies to enhance your browsing experience.
            These technologies help us:
          </Paragraph>
          <List>
            <li>Remember your preferences and settings</li>
            <li>Analyze website traffic and user behavior</li>
            <li>Improve website functionality</li>
          </List>
          <Paragraph>
            You can control cookie settings through your browser preferences.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Third-Party Services</SectionTitle>
          <Paragraph>
            We may use third-party services that collect, monitor, and analyze data to improve our
            services. These may include:
          </Paragraph>
          <List>
            <li><strong>Web3 Providers:</strong> Wallet connection services (e.g., WalletConnect, MetaMask)</li>
            <li><strong>Analytics Services:</strong> Tools to understand website usage patterns</li>
            <li><strong>Hosting Services:</strong> Infrastructure providers for website hosting</li>
          </List>
          <Paragraph>
            These third parties have their own privacy policies, and we are not responsible for their practices.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Data Security</SectionTitle>
          <Paragraph>
            We implement appropriate technical and organizational measures to protect your personal
            information against unauthorized access, alteration, disclosure, or destruction. However,
            no method of transmission over the internet is 100% secure.
          </Paragraph>
          <Paragraph>
            <strong>Important Security Reminders:</strong>
          </Paragraph>
          <List>
            <li>Never share your private keys or seed phrases</li>
            <li>Always verify smart contract addresses before interacting</li>
            <li>Be cautious of phishing attempts and scams</li>
            <li>Use hardware wallets for large holdings</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>8. Data Retention</SectionTitle>
          <Paragraph>
            We retain your personal information only for as long as necessary to fulfill the purposes
            outlined in this Privacy Policy, unless a longer retention period is required by law.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Your Rights</SectionTitle>
          <Paragraph>
            Depending on your jurisdiction, you may have the following rights regarding your personal data:
          </Paragraph>
          <List>
            <li>Right to access your personal information</li>
            <li>Right to correct inaccurate data</li>
            <li>Right to request deletion of your data</li>
            <li>Right to object to data processing</li>
            <li>Right to data portability</li>
          </List>
          <Paragraph>
            To exercise these rights, please contact us at hello@pepesdog.box
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Children's Privacy</SectionTitle>
          <Paragraph>
            Our website is not intended for individuals under 18 years of age. We do not knowingly
            collect personal information from children. If you believe we have collected data from
            a minor, please contact us immediately.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. International Data Transfers</SectionTitle>
          <Paragraph>
            Your information may be transferred to and processed in countries other than your country
            of residence. We ensure appropriate safeguards are in place to protect your data in
            accordance with this Privacy Policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Changes to This Privacy Policy</SectionTitle>
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any changes by
            posting the new policy on this page and updating the "Last Updated" date. We encourage you
            to review this Privacy Policy periodically.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13. GDPR Compliance (EU Users)</SectionTitle>
          <Paragraph>
            If you are located in the European Union, we process your data based on the following legal grounds:
          </Paragraph>
          <List>
            <li>Your consent (which you can withdraw at any time)</li>
            <li>Performance of a contract</li>
            <li>Compliance with legal obligations</li>
            <li>Our legitimate interests</li>
          </List>
        </Section>

        <ContactInfo>
          <Paragraph>
            <strong>Questions or Concerns About Privacy?</strong><br />
            Contact us at: <a href="mailto:hello@pepesdog.box">hello@pepesdog.box</a>
          </Paragraph>
        </ContactInfo>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PrivacyPage;
